/** Client <-> server protocol for the meet room. */
import type { EventId } from './events.js';
import type { AthleteSim } from './sim/athlete.js';
import type { RunKey } from './input.js';

export interface RoomInfo {
  code: string;
  isPublic: boolean;
}
export const PUBLIC_ROOM = 'public';
export function normaliseRoomCode(value: string): string | null {
  const code = value.trim().toUpperCase();
  return /^[A-Z0-9]{4}$/.test(code) ? code : null;
}

export type MeetPhase = 'lobby' | 'intro' | 'event' | 'results' | 'podium';
export type Programme = 'full' | 'day1' | 'day2' | EventId;
export type BotLevel = 'club' | 'county' | 'national' | 'olympic';
export const BOT_LEVELS: { id: BotLevel; name: string; blurb: string }[] = [
  { id: 'club', name: 'Club', blurb: 'Weekend athletes' },
  { id: 'county', name: 'County', blurb: 'Decent all-rounders' },
  { id: 'national', name: 'National', blurb: 'Championship standard' },
  { id: 'olympic', name: 'Olympic', blurb: 'Daley territory' },
];

export interface RoomOptions {
  strictHurdles?: boolean;
  programme: Programme;
  fillWithBots: boolean;
  botLevel: BotLevel;
}

export interface PlayerInfo {
  id: string;
  name: string;
  kit: string;
  isBot: boolean;
  isHost: boolean;
  connected?: boolean;
  ready: boolean;
  lives: number;
  eliminated: boolean;
  /** Meet total so far. */
  total: number;
}

/** One athlete's simulated state, broadcast at 10 Hz. */
export interface AthleteSnap {
  id: string;
  x: number;
  y: number;
  comY: number;
  v: number;
  cadence: number;
  phase: string;
  angle: number;
  /** Throws: wind-up power 0..1. */
  power: number;
  attempt: number;
  /** Best mark so far in this event (field) or finishing time (track). */
  mark: number | null;
  done: boolean;
  /** Field events: bar height / per-attempt results. */
  bar: number;
  misses: number;
  results: (number | null)[];
  falseStart: boolean;
  runX?: number;
  /** Javelin run-up speed, separate from the projectile's flight speed. */
  runV?: number;
  heading?: number;
  /** Projectile's current flight-path pitch, in radians; retained at impact. */
  projectilePitch?: number;
  hits?: number;
  hurdle?: number;
}

export interface EventResultRow {
  /** False for eliminated athletes retained in overall standings. */
  participated?: boolean;
  id: string;
  name: string;
  kit: string;
  isBot: boolean;
  mark: number | null;
  points: number;
  /** Place in this event. */
  place: number;
  /** Meet total after this event and standing. */
  total: number;
  standing: number;
  passed: boolean;
  lives: number;
  eliminated: boolean;
}

export interface MeetResultRow {
  playerKey?: string;
  id: string;
  name: string;
  kit: string;
  isBot: boolean;
  total: number;
  place: number;
  marks: Partial<Record<EventId, number | null>>;
  points: Partial<Record<EventId, number>>;
  /** League points earned (humans only, placed among humans). */
  leaguePoints: number;
  /** Highest single-event points in the meet, and whether it earned the bonus. */
  bestEvent: number;
  bestEventBonus: boolean;
  eliminated: boolean;
}

export interface LeagueRow {
  name: string;
  meets: number;
  wins: number;
  podiums: number;
  points: number;
  bestTotal: number;
  lastPlayed: string;
}

export interface RecentMeet {
  id: number;
  playedAt: string;
  winner: string;
  athletes: number;
  bestTotal: number;
  programme: string;
}

export interface InputBatch {
  /** Event index in the meet, so late packets from the previous event are dropped. */
  seq?: number;
  eventIndex: number;
  presses: { key: RunKey; t: number }[];
  actions: { down: boolean; t: number }[];
}

export type ClientMessage =
  | {
      type: 'join';
      name: string;
      kit: string;
      token?: string;
      resume?: string;
      room?: string;
      createRoom?: boolean;
    }
  | { type: 'setKit'; kit: string }
  | { type: 'setOptions'; options: Partial<RoomOptions> }
  | { type: 'ready'; ready: boolean }
  | { type: 'start' }
  | { type: 'setHeight'; height: number }
  | { type: 'nextSeason' }
  | { type: 'leave' }
  | { type: 'input'; batch: InputBatch }
  | { type: 'backToLobby' }
  /** Field events: play the active bot's turn out instantly instead of watching it. */
  | { type: 'skipBot' }
  | { type: 'requestLeague' }
  | { type: 'ping'; t: number };

export interface EventStartMsg {
  type: 'eventStart';
  event: EventId;
  index: number;
  total: number;
  /** Server time (ms) that sim time zero refers to: the gun for track, the start for field. */
  refAt: number;
  /** Track events: server time of the "Set" cue. */
  setAt: number;
  season: number;
  standard: number;
  /** High jump: opening bar height. */
  startHeight: number;
  /** Lane per athlete for track events; field events use it for the waiting order. */
  lanes: Record<string, number>;
  /** Field events are played one athlete at a time. */
  turnBased: boolean;
}

/** Field events run turn by turn: whose go it is, and when their attempt clock started. */
export interface TurnMsg {
  type: 'turn';
  id: string;
  /** Server time (ms) of attempt-time zero for this turn. */
  refAt: number;
  attempt: number;
  bar: number;
  /** Seconds the athlete has to start the run-up. */
  clock: number;
}

export type ServerMessage =
  | { type: 'welcome'; id: string; serverTime: number; resume?: string; room?: RoomInfo }
  | { type: 'lobby'; players: PlayerInfo[]; options: RoomOptions; phase: MeetPhase }
  | EventStartMsg
  | TurnMsg
  | { type: 'correction'; index: number; ack: number; sim: AthleteSim; serverTime: number }
  | { type: 'snapshot'; serverTime: number; athletes: AthleteSnap[]; activeId: string | null }
  | {
      type: 'eventResults';
      meetId: string;
      event: EventId;
      index: number;
      total: number;
      results: EventResultRow[];
      nextAt: number;
    }
  | { type: 'podium'; results: MeetResultRow[]; programme: Programme }
  | { type: 'league'; rows: LeagueRow[]; recent: RecentMeet[] }
  | { type: 'error'; message: string }
  | { type: 'pong'; t: number; serverTime: number };

export const MAX_PLAYERS = 6;
